import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../../api/api';
import {
  EventVenue,
  EventVenueBookingAdmin,
  EventVenueBookingPolicy,
  EventVenueMenuView,
  EventVenueSaleScope,
} from '../../types';

interface DepositForm {
  amount: string;
  method: string;
  reference: string;
  notes: string;
}

interface PolicyForm {
  onlineBookingsEnabled: boolean;
  customerMenuEditingEnabled: boolean;
  requiredDepositAmount: string;
  depositDueHours: string;
  minimumBookingNoticeDays: string;
  maximumBookingAdvanceDays: string;
  menuChangeDeadlineDays: string;
}

const emptyDeposit: DepositForm = {
  amount: '',
  method: 'Банківський переказ',
  reference: '',
  notes: '',
};

const emptyPolicy: PolicyForm = {
  onlineBookingsEnabled: false,
  customerMenuEditingEnabled: false,
  requiredDepositAmount: '',
  depositDueHours: '',
  minimumBookingNoticeDays: '',
  maximumBookingAdvanceDays: '',
  menuChangeDeadlineDays: '',
};

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.response?.data?.title || fallback;

const typedErrorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError<{ message?: string; title?: string }>(error)) return fallback;
  return error.response?.data?.message || error.response?.data?.title || fallback;
};

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('uk-UA', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Kyiv',
  }).format(new Date(value));

const statusLabels: Record<string, string> = {
  PendingDeposit: 'Очікує завдаток',
  Confirmed: 'Підтверджено',
  Rejected: 'Відхилено',
  Cancelled: 'Скасовано',
};

const EventVenueBookingsPage: React.FC = () => {
  const [requestedVenueId] = useState(() => new URLSearchParams(window.location.search).get('venueId'));
  const [venues, setVenues] = useState<EventVenue[]>([]);
  const [bookings, setBookings] = useState<EventVenueBookingAdmin[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const selectedVenueIdRef = useRef('');
  const [policy, setPolicy] = useState<EventVenueBookingPolicy | null>(null);
  const [policyForm, setPolicyForm] = useState<PolicyForm>(emptyPolicy);
  const [saleScope, setSaleScope] = useState<EventVenueSaleScope | null>(null);
  const [scopeLoad, setScopeLoad] = useState<'idle' | 'loading' | 'loaded' | 'failed'>('idle');
  const scopeRequest = useRef(0);
  const [operatingGroups, setOperatingGroups] = useState<Array<{ id: number; name: string; isArchived: boolean }>>([]);
  const [scopeGroupId, setScopeGroupId] = useState('');
  const [useScopedCatalog, setUseScopedCatalog] = useState(false);
  const [savingScope, setSavingScope] = useState(false);
  const [scopeError, setScopeError] = useState('');
  const [scopeNotice, setScopeNotice] = useState('');
  const [depositBookingId, setDepositBookingId] = useState<number | null>(null);
  const [deposit, setDeposit] = useState<DepositForm>(emptyDeposit);
  const [loading, setLoading] = useState(true);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  const [menuViews, setMenuViews] = useState<Record<number, EventVenueMenuView>>({});
  const [menuOverrides, setMenuOverrides] = useState<Record<number, string>>({});
  const [openMenuBookingId, setOpenMenuBookingId] = useState<number | null>(null);
  const [loadingMenuBookingId, setLoadingMenuBookingId] = useState<number | null>(null);
  const [savingMenuPermission, setSavingMenuPermission] = useState(false);
  const [menuError, setMenuError] = useState('');
  const [menuNotice, setMenuNotice] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadBookings = useCallback(async () => {
    const { data } = await api.get<EventVenueBookingAdmin[]>('/event-venue-bookings/admin',
      requestedVenueId ? { params: { venueId: requestedVenueId } } : undefined);
    setBookings(data);
  }, [requestedVenueId]);

  const clearSaleScope = useCallback((venueId: string) => {
    scopeRequest.current += 1;
    setSaleScope(null);
    setScopeGroupId('');
    setUseScopedCatalog(false);
    setScopeNotice('');
    setScopeError('');
    setScopeLoad(venueId ? 'loading' : 'idle');
  }, []);

  // Responses are applied only to the latest request for the venue that requested them.
  const loadSaleScope = useCallback(async (venueId: string): Promise<'loaded' | 'failed' | 'stale'> => {
    clearSaleScope(venueId);
    if (!venueId) return 'stale';
    const request = scopeRequest.current;
    try {
      const [scopeResponse, groupsResponse] = await Promise.all([
        api.get<EventVenueSaleScope>(`/event-venue-bookings/sale-scope/${venueId}`),
        api.get<Array<{ id: number; name: string; isArchived: boolean }>>('/operating-groups'),
      ]);
      if (request !== scopeRequest.current || scopeResponse.data.eventVenueId !== Number(venueId)) return 'stale';
      setSaleScope(scopeResponse.data);
      setScopeGroupId(scopeResponse.data.operatingGroupId ? String(scopeResponse.data.operatingGroupId) : '');
      setUseScopedCatalog(scopeResponse.data.useOperatingGroupSaleCatalog);
      setOperatingGroups(groupsResponse.data);
      setScopeLoad('loaded');
      return 'loaded';
    } catch (requestError: unknown) {
      if (request !== scopeRequest.current) return 'stale';
      setScopeLoad('failed');
      setScopeError(typedErrorMessage(requestError, 'Не вдалося завантажити контекст продажу.'));
      return 'failed';
    }
  }, [clearSaleScope]);

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get<EventVenue[]>('/eventvenues'),
      api.get<EventVenueBookingAdmin[]>('/event-venue-bookings/admin',
        requestedVenueId ? { params: { venueId: requestedVenueId } } : undefined),
    ])
      .then(([venueResponse, bookingResponse]) => {
        if (!active) return;
        setVenues(venueResponse.data);
        setBookings(bookingResponse.data);
        const initialVenueId = requestedVenueId
          ? String(venueResponse.data.find(venue => String(venue.id) === requestedVenueId)?.id || '')
          : venueResponse.data[0] ? String(venueResponse.data[0].id) : '';
        selectedVenueIdRef.current = initialVenueId;
        setSelectedVenueId(initialVenueId);
      })
      .catch((requestError) => {
        if (active) setError(getErrorMessage(requestError, 'Не вдалося завантажити бронювання та зали.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [requestedVenueId]);

  useEffect(() => {
    if (!selectedVenueId) {
      setPolicy(null);
      setPolicyForm(emptyPolicy);
      setSaleScope(null);
      return;
    }
    let active = true;
    setPolicy(null);
    setPolicyForm(emptyPolicy);
    api.get<EventVenueBookingPolicy>(`/event-venue-bookings/policy/${selectedVenueId}`)
      .then(({ data }) => {
        if (active) {
          setPolicy(data);
          setPolicyForm({
            onlineBookingsEnabled: data.onlineBookingsEnabled,
            customerMenuEditingEnabled: data.customerMenuEditingEnabled,
            requiredDepositAmount: String(data.requiredDepositAmount),
            depositDueHours: String(data.depositDueHours),
            minimumBookingNoticeDays: String(data.minimumBookingNoticeDays),
            maximumBookingAdvanceDays: String(data.maximumBookingAdvanceDays),
            menuChangeDeadlineDays: String(data.menuChangeDeadlineDays),
          });
        }
      })
      .catch((requestError) => {
        if (active) setError(getErrorMessage(requestError, 'Не вдалося завантажити політику бронювання.'));
      });
    return () => { active = false; };
  }, [selectedVenueId]);

  useEffect(() => {
    void loadSaleScope(selectedVenueId);
  }, [selectedVenueId, loadSaleScope]);

  useEffect(() => {
    const requests = scopeRequest;
    return () => { requests.current += 1; };
  }, []);

  const savePolicy = async (event: FormEvent) => {
    event.preventDefault();
    if (!policy || policy.eventVenueId !== Number(selectedVenueIdRef.current)) return;
    const savingVenueId = policy.eventVenueId;
    setError('');
    setNotice('');
    setSavingPolicy(true);
    try {
      const { data } = await api.put<EventVenueBookingPolicy>(
        `/event-venue-bookings/policy/${savingVenueId}`,
        {
          onlineBookingsEnabled: policyForm.onlineBookingsEnabled,
          customerMenuEditingEnabled: policyForm.customerMenuEditingEnabled,
          requiredDepositAmount: Number(policyForm.requiredDepositAmount),
          depositDueHours: Number(policyForm.depositDueHours),
          minimumBookingNoticeDays: Number(policyForm.minimumBookingNoticeDays),
          maximumBookingAdvanceDays: Number(policyForm.maximumBookingAdvanceDays),
          menuChangeDeadlineDays: Number(policyForm.menuChangeDeadlineDays),
        }
      );
      if (selectedVenueIdRef.current === String(savingVenueId)) {
        setPolicy(data);
        setPolicyForm({
          onlineBookingsEnabled: data.onlineBookingsEnabled,
          customerMenuEditingEnabled: data.customerMenuEditingEnabled,
          requiredDepositAmount: String(data.requiredDepositAmount),
          depositDueHours: String(data.depositDueHours),
          minimumBookingNoticeDays: String(data.minimumBookingNoticeDays),
          maximumBookingAdvanceDays: String(data.maximumBookingAdvanceDays),
          menuChangeDeadlineDays: String(data.menuChangeDeadlineDays),
        });
        setNotice('Політику бронювання збережено.');
      }
    } catch (requestError) {
      if (selectedVenueIdRef.current === String(savingVenueId)) {
        setError(getErrorMessage(requestError, 'Не вдалося зберегти політику бронювання.'));
      }
    } finally {
      setSavingPolicy(false);
    }
  };

  const saveSaleScope = async (event: FormEvent) => {
    event.preventDefault();
    if (!saleScope || scopeLoad !== 'loaded' || saleScope.eventVenueId !== Number(selectedVenueIdRef.current)) return;
    const venueId = saleScope.eventVenueId;
    const request = scopeRequest.current;
    setSavingScope(true);
    setScopeError('');
    setScopeNotice('');
    try {
      const { data } = await api.put<EventVenueSaleScope>(
        `/event-venue-bookings/sale-scope/${venueId}`,
        {
          version: saleScope.version,
          operatingGroupId: scopeGroupId ? Number(scopeGroupId) : null,
          useOperatingGroupSaleCatalog: useScopedCatalog,
        }
      );
      if (request === scopeRequest.current && selectedVenueIdRef.current === String(venueId)) {
        setSaleScope(data);
        setScopeGroupId(data.operatingGroupId ? String(data.operatingGroupId) : '');
        setUseScopedCatalog(data.useOperatingGroupSaleCatalog);
        setScopeNotice('Контекст продажу збережено. Існуючі пропозиції меню не змінюються.');
      }
    } catch (requestError: unknown) {
      if (request !== scopeRequest.current || selectedVenueIdRef.current !== String(venueId)) return;
      const message = typedErrorMessage(requestError, 'Не вдалося зберегти контекст продажу.');
      if (axios.isAxiosError(requestError) && requestError.response?.status === 409) {
        const reloaded = await loadSaleScope(String(venueId));
        if (reloaded === 'loaded') setScopeError(message);
        else if (reloaded === 'failed') {
          setScopeError('Контекст змінився, а актуальну версію не вдалося завантажити. Повторіть завантаження.');
        }
      } else {
        setScopeError(message);
      }
    } finally {
      setSavingScope(false);
    }
  };

  const confirmDeposit = async (event: FormEvent) => {
    event.preventDefault();
    if (depositBookingId === null) return;
    setError('');
    setNotice('');
    setSavingBooking(true);
    try {
      await api.put(`/event-venue-bookings/admin/${depositBookingId}/confirm-deposit`, {
        amount: Number(deposit.amount),
        method: deposit.method.trim(),
        reference: deposit.reference.trim(),
        notes: deposit.notes.trim(),
      });
      await loadBookings();
      setDepositBookingId(null);
      setDeposit(emptyDeposit);
      setNotice('Завдаток зафіксовано, бронювання підтверджено.');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Не вдалося підтвердити завдаток.'));
    } finally {
      setSavingBooking(false);
    }
  };

  const updateStatus = async (bookingId: number, action: 'reject' | 'cancel') => {
    const actionLabel = action === 'reject' ? 'відхилити' : 'скасувати';
    if (!window.confirm(`Підтвердити дію: ${actionLabel} заявку?`)) return;
    setError('');
    setNotice('');
    setSavingBooking(true);
    try {
      await api.put(`/event-venue-bookings/admin/${bookingId}/${action}`, { notes: '' });
      await loadBookings();
      setNotice(action === 'reject' ? 'Заявку відхилено.' : 'Бронювання скасовано.');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Не вдалося змінити стан заявки.'));
    } finally {
      setSavingBooking(false);
    }
  };

  const loadAdminMenu = async (bookingId: number) => {
    setLoadingMenuBookingId(bookingId);
    setMenuError('');
    try {
      const { data } = await api.get<EventVenueMenuView>(`/event-venue-bookings/admin/${bookingId}/menu`);
      setMenuViews((current) => ({ ...current, [bookingId]: data }));
      setMenuOverrides((current) => ({
        ...current,
        [bookingId]: data.customerMenuEditingOverride === null
          ? 'inherit'
          : data.customerMenuEditingOverride ? 'allow' : 'deny',
      }));
      return data;
    } catch (requestError) {
      setMenuError(getErrorMessage(requestError, 'Не вдалося завантажити меню клієнта.'));
      return null;
    } finally {
      setLoadingMenuBookingId(null);
    }
  };

  const saveMenuPermission = async (bookingId: number) => {
    const currentView = menuViews[bookingId];
    if (!currentView) return;
    const selectedOverride = menuOverrides[bookingId] || 'inherit';
    setSavingMenuPermission(true);
    setMenuError('');
    setMenuNotice('');
    try {
      const { data } = await api.put<EventVenueMenuView>(
        `/event-venue-bookings/admin/${bookingId}/menu-permission`,
        {
          version: currentView.version,
          customerMenuEditingOverride: selectedOverride === 'inherit'
            ? null
            : selectedOverride === 'allow',
        }
      );
      setMenuViews((current) => ({ ...current, [bookingId]: data }));
      setMenuOverrides((current) => ({
        ...current,
        [bookingId]: data.customerMenuEditingOverride === null
          ? 'inherit'
          : data.customerMenuEditingOverride ? 'allow' : 'deny',
      }));
      setMenuNotice('Дозвіл редагування меню оновлено.');
    } catch (requestError) {
      if (axios.isAxiosError(requestError) && requestError.response?.status === 409) {
        const refreshed = await loadAdminMenu(bookingId);
        if (refreshed) setMenuNotice('Версія бронювання змінилася. Перевірте актуальні дані та явно повторіть зміну дозволу.');
      } else {
        setMenuError(getErrorMessage(requestError, 'Не вдалося змінити дозвіл редагування меню.'));
      }
    } finally {
      setSavingMenuPermission(false);
    }
  };

  const changePolicy = (field: keyof PolicyForm, value: boolean | string) => {
    setPolicyForm((current) => ({ ...current, [field]: value }));
  };

  return (
    <main className="page-shell page-shell--wide" style={{ maxWidth: 1200 }}>
      <h2>Бронювання та політика залів</h2>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {loading ? <p>Завантаження...</p> : (
        <>
          <section aria-labelledby="venue-policy-title" style={{
            margin: '16px 0 28px',
            padding: 16,
            background: 'var(--crm-surface)',
            border: '1px solid var(--crm-border)',
            borderRadius: 8,
            boxShadow: 'var(--crm-shadow-sm)',
          }}>
            <h3 id="venue-policy-title" style={{ marginTop: 0 }}>Налаштування онлайн-бронювання</h3>
            {venues.length === 0 ? <p>Спочатку створіть місце проведення.</p> : (
              <>
                <label style={{ display: 'block', maxWidth: 520, marginBottom: 14 }}>
                  Місце проведення
                  <select
                    value={selectedVenueId}
                    disabled={savingPolicy || savingScope}
                    onChange={(event) => {
                      selectedVenueIdRef.current = event.target.value;
                      clearSaleScope(event.target.value);
                      setSelectedVenueId(event.target.value);
                    }}
                  >
                    {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
                  </select>
                </label>
                {policy ? (
                  <form onSubmit={savePolicy} className="legacy-crud-page">
                    <div className="legacy-crud-page__form-grid legacy-crud-page__form-grid--two">
                      <label>
                        <input
                          type="checkbox"
                          checked={policyForm.onlineBookingsEnabled}
                          onChange={(event) => changePolicy('onlineBookingsEnabled', event.target.checked)}
                        />
                        Увімкнути онлайн-бронювання
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={policyForm.customerMenuEditingEnabled}
                          onChange={(event) => changePolicy('customerMenuEditingEnabled', event.target.checked)}
                        />
                        Дозволити клієнтам редагувати меню онлайн
                      </label>
                      <label>
                        Обов’язковий завдаток (₴)
                        <input
                          type="number"
                          min={0}
                          max={1000000}
                          step="0.01"
                          value={policyForm.requiredDepositAmount}
                          onChange={(event) => changePolicy('requiredDepositAmount', event.target.value)}
                        />
                      </label>
                      <label>
                        Годин на внесення завдатку
                        <input
                          type="number"
                          min={1}
                          max={720}
                          step={1}
                          value={policyForm.depositDueHours}
                          onChange={(event) => changePolicy('depositDueHours', event.target.value)}
                        />
                      </label>
                      <label>
                        Мінімальна завчасність бронювання (днів)
                        <input
                          type="number"
                          min={0}
                          max={365}
                          step={1}
                          value={policyForm.minimumBookingNoticeDays}
                          onChange={(event) => changePolicy('minimumBookingNoticeDays', event.target.value)}
                        />
                      </label>
                      <label>
                        Максимальний горизонт бронювання (днів)
                        <input
                          type="number"
                          min={1}
                          max={730}
                          step={1}
                          value={policyForm.maximumBookingAdvanceDays}
                          onChange={(event) => changePolicy('maximumBookingAdvanceDays', event.target.value)}
                        />
                      </label>
                      <label>
                        Крайній строк зміни меню (днів до події)
                        <input
                          type="number"
                          min={0}
                          max={365}
                          step={1}
                          value={policyForm.menuChangeDeadlineDays}
                          onChange={(event) => changePolicy('menuChangeDeadlineDays', event.target.value)}
                        />
                      </label>
                    </div>
                    <p>
                      Крайній строк є виключним: зміни доступні до початку вказаної дати за київським часом.
                      Дозвіл залу можна окремо перевизначити для кожного бронювання; строк, статус і завдаток
                      усе одно обмежують редагування.
                    </p>
                    <button type="submit" disabled={savingPolicy || !policy}>
                      {savingPolicy ? 'Збереження...' : 'Зберегти політику'}
                    </button>
                  </form>
                ) : <p>Завантаження політики...</p>}
                <form onSubmit={saveSaleScope} style={{ marginTop: 24 }}>
                  <h4>Операційний контекст каталогу продажу</h4>
                  <p>
                    Залиште контекстний режим вимкненим, щоб зберегти глобальний затверджений каталог.
                    Увімкнений режим показує лише позиції, вибрані для вказаної групи та затверджені глобально.
                  </p>
                  {scopeError && <p role="alert">{scopeError}</p>}
                  {scopeNotice && <p role="status">{scopeNotice}</p>}
                  {scopeLoad === 'failed' ? (
                    <button type="button" disabled={savingScope} onClick={() => void loadSaleScope(selectedVenueId)}>
                      Повторити завантаження контексту
                    </button>
                  ) : !saleScope || scopeLoad !== 'loaded' || saleScope.eventVenueId !== Number(selectedVenueId) ? (
                    <p>Завантаження контексту продажу...</p>
                  ) : (
                    <>
                      <label style={{ display: 'block', maxWidth: 520, marginBottom: 12 }}>
                        Операційна група
                        <select
                          value={scopeGroupId}
                          disabled={savingScope}
                          onChange={(event) => setScopeGroupId(event.target.value)}
                        >
                          <option value="">Не прив’язано</option>
                          {operatingGroups.filter((group) => !group.isArchived).map((group) =>
                            <option key={group.id} value={group.id}>{group.name}</option>)}
                        </select>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <input
                          type="checkbox"
                          checked={useScopedCatalog}
                          disabled={savingScope}
                          onChange={(event) => setUseScopedCatalog(event.target.checked)}
                        />
                        Використовувати каталог вибраної операційної групи
                      </label>
                      <button type="submit" disabled={savingScope || (useScopedCatalog && !scopeGroupId)}>
                        {savingScope ? 'Збереження...' : 'Зберегти контекст каталогу'}
                      </button>
                    </>
                  )}
                </form>
              </>
            )}
          </section>

          <section aria-labelledby="venue-bookings-title">
            <h3 id="venue-bookings-title">Заявки та бронювання</h3>
            {bookings.length === 0 ? <p>Заявок на бронювання поки немає.</p> : (
              <div style={{ display: 'grid', gap: 14 }}>
                {bookings.map((booking) => (
                  <article key={booking.id} style={{
                    padding: 16,
                    background: 'var(--crm-surface)',
                    color: 'var(--crm-text)',
                    border: '1px solid var(--crm-border)',
                    borderRadius: 8,
                    boxShadow: 'var(--crm-shadow-sm)',
                  }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8 }}>
                      <h4 style={{ margin: 0 }}>#{booking.id} · {booking.venueName} · {statusLabels[booking.status] || booking.status}</h4>
                      <strong>{booking.eventDate} · {booking.startTime.slice(0, 5)}–{booking.endTime.slice(0, 5)}</strong>
                    </div>
                    <p>
                      {booking.customerName} · {booking.phone}{booking.email ? ` · ${booking.email}` : ''} · {booking.guestCount} гостей
                    </p>
                    <p>
                      Завдаток: {booking.depositAmount === null
                        ? `не зафіксовано (мінімум ${booking.requiredDepositAmount.toFixed(2)} ₴)`
                        : `${booking.depositAmount.toFixed(2)} ₴ · ${booking.depositMethod}`}
                      {' · '}строк внесення: {formatDateTime(booking.depositDueAtUtc)}
                    </p>
                    {booking.customerNotes && <p>Побажання: {booking.customerNotes}</p>}
                    {booking.adminNotes && <p>Примітки адміністратора: {booking.adminNotes}</p>}
                    <button
                      type="button"
                      onClick={() => {
                        if (openMenuBookingId === booking.id) {
                          setOpenMenuBookingId(null);
                          return;
                        }
                        setOpenMenuBookingId(booking.id);
                        if (!menuViews[booking.id]) void loadAdminMenu(booking.id);
                      }}
                    >
                      {openMenuBookingId === booking.id ? 'Закрити меню клієнта' : 'Переглянути меню клієнта'}
                    </button>
                    {openMenuBookingId === booking.id && (
                      <section aria-label={`Меню клієнта, бронювання ${booking.id}`} style={{
                        marginTop: 12,
                        padding: 14,
                        border: '1px solid var(--crm-border)',
                        borderRadius: 8,
                        background: 'var(--crm-surface-alt)',
                      }}>
                        {menuError && <p role="alert">{menuError}</p>}
                        {menuNotice && <p role="status">{menuNotice}</p>}
                        {loadingMenuBookingId === booking.id ? <p>Завантаження меню...</p> : null}
                        {menuViews[booking.id] && (
                          <>
                            <p>
                              Версія бронювання: {menuViews[booking.id].version} ·{' '}
                              {menuViews[booking.id].canEdit ? 'клієнт може редагувати' : 'редагування зараз недоступне'} ·{' '}
                              строк: {menuViews[booking.id].editDeadlineDate}
                            </p>
                            {menuViews[booking.id].proposal ? (
                              <>
                                <ul>
                                  {menuViews[booking.id].proposal!.lines.map((line, index) => (
                                    <li key={`${line.itemType}-${line.itemId}-${index}`}>
                                      {line.name} — {line.quantity} {line.unit} · {line.lineTotal.toFixed(2)} ₴
                                    </li>
                                  ))}
                                </ul>
                                <strong>Разом: {menuViews[booking.id].proposal!.totalPrice.toFixed(2)} ₴</strong>
                              </>
                            ) : <p>Клієнт ще не зберіг меню.</p>}
                            <label style={{ display: 'block', maxWidth: 420, marginTop: 12 }}>
                              Дозвіл клієнту змінювати меню
                              <select
                                value={menuOverrides[booking.id] || 'inherit'}
                                disabled={savingMenuPermission}
                                onChange={(event) => setMenuOverrides((current) => ({
                                  ...current,
                                  [booking.id]: event.target.value,
                                }))}
                              >
                                <option value="inherit">Спадкувати політику залу</option>
                                <option value="allow">Дозволити для цього бронювання</option>
                                <option value="deny">Заборонити для цього бронювання</option>
                              </select>
                            </label>
                            <button
                              type="button"
                              disabled={savingMenuPermission}
                              onClick={() => void saveMenuPermission(booking.id)}
                              style={{ marginTop: 8, minHeight: 44 }}
                            >
                              {savingMenuPermission ? 'Збереження...' : 'Зберегти дозвіл'}
                            </button>
                          </>
                        )}
                      </section>
                    )}

                    {booking.status === 'PendingDeposit' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button
                          type="button"
                          disabled={savingBooking}
                          onClick={() => {
                            setDepositBookingId(booking.id);
                            setDeposit({ ...emptyDeposit, amount: String(booking.requiredDepositAmount) });
                          }}
                        >
                          Зафіксувати завдаток і підтвердити
                        </button>
                        <button type="button" disabled={savingBooking} onClick={() => updateStatus(booking.id, 'reject')}>
                          Відхилити
                        </button>
                      </div>
                    )}
                    {booking.status === 'Confirmed' && (
                      <button type="button" disabled={savingBooking} onClick={() => updateStatus(booking.id, 'cancel')}>
                        Скасувати бронювання
                      </button>
                    )}

                    {depositBookingId === booking.id && (
                      <form onSubmit={confirmDeposit} className="legacy-crud-page" style={{
                        marginTop: 14,
                        padding: 14,
                        border: '1px solid var(--crm-border)',
                        borderRadius: 8,
                      }}>
                        <div className="legacy-crud-page__form-grid legacy-crud-page__form-grid--two">
                          <label>
                            Фактично отримано (₴)
                            <input
                              type="number"
                              required
                              min={booking.requiredDepositAmount}
                              max={1000000}
                              step="0.01"
                              value={deposit.amount}
                              onChange={(event) => setDeposit((current) => ({ ...current, amount: event.target.value }))}
                            />
                          </label>
                          <label>
                            Спосіб оплати
                            <input
                              required
                              maxLength={50}
                              value={deposit.method}
                              onChange={(event) => setDeposit((current) => ({ ...current, method: event.target.value }))}
                            />
                          </label>
                          <label>
                            Референс платежу
                            <input
                              maxLength={100}
                              value={deposit.reference}
                              onChange={(event) => setDeposit((current) => ({ ...current, reference: event.target.value }))}
                            />
                          </label>
                          <label>
                            Примітка адміністратора
                            <input
                              maxLength={1000}
                              value={deposit.notes}
                              onChange={(event) => setDeposit((current) => ({ ...current, notes: event.target.value }))}
                            />
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <button type="submit" disabled={savingBooking}>
                            {savingBooking ? 'Збереження...' : 'Підтвердити отримання'}
                          </button>
                          <button type="button" disabled={savingBooking} onClick={() => setDepositBookingId(null)}>
                            Закрити
                          </button>
                        </div>
                      </form>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
};

export default EventVenueBookingsPage;
